import {
  announceAddedToCart,
  formatMoney,
  formatDecimal
} from "./chunk-0wwf8h27.js";
import {
  readableCartMessage
} from "./chunk-eqgj88e9.js";
import {
  readJsonScript,
  isRecord,
  readString,
  readNumber,
  cart
} from "./chunk-9v1sdekd.js";
import {
  isMobileViewport,
  openCartDrawer
} from "./chunk-s8fmrfaa.js";
import {
  arrowNavigate
} from "./chunk-zpd9q9kk.js";
import {
  subscriptionTypeFor,
  captureFocus
} from "./chunk-fn5j2c34.js";

// src/pages/pdp-v2/attributes.ts
var pdpAttributes = {
  animKey: "data-anim-key",
  close: "data-pdp-close",
  dock: "data-pdp-dock",
  dot: "data-pdp-dot",
  error: "data-pdp-error",
  expanded: "data-expanded",
  index: "data-index",
  labels: "data-pdp-labels",
  lightbox: "data-pdp-lightbox",
  loading: "data-loading",
  mediaId: "data-media-id",
  next: "data-pdp-next",
  prev: "data-pdp-prev",
  product: "data-pdp-product",
  selected: "data-selected",
  slide: "data-pdp-slide",
  stickyAnchor: "data-pdp-sticky-anchor",
  stuck: "data-stuck",
  toast: "data-pdp-toast",
  track: "data-pdp-track",
  unavailable: "data-unavailable",
  viewport: "data-pdp-viewport",
  zoom: "data-pdp-zoom"
};

// src/pages/pdp-v2/gallery.ts
var MEDIA_CHANGE_EVENT = "pdp:media-change";
var SWIPE_THRESHOLD = 40;
var BODY_MODAL_CLASS = "modal-opened";
var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
var htmlElements = (root, selector) => [...root.querySelectorAll(selector)].filter((node) => node instanceof HTMLElement);
var buttonElements = (root, selector) => [...root.querySelectorAll(selector)].filter((node) => node instanceof HTMLButtonElement);
var readCarousel = (viewport) => {
  const track = viewport.querySelector(`[${pdpAttributes.track}]`);
  if (!(track instanceof HTMLElement))
    return null;
  return {
    track,
    slides: htmlElements(viewport, `[${pdpAttributes.slide}]`),
    dots: buttonElements(viewport, `[${pdpAttributes.dot}]`)
  };
};
var dotIndex = (dot) => {
  const raw = dot.getAttribute(pdpAttributes.index);
  if (raw === null)
    return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

class PdpGallery extends HTMLElement {
  index = 0;
  count = 0;
  carousels = [];
  lightbox = null;
  returnFocus = null;
  pointerStart = null;
  connectedCallback() {
    const viewports = htmlElements(this, `[${pdpAttributes.viewport}]`);
    this.carousels = viewports.map((viewport) => readCarousel(viewport)).filter((carousel) => carousel !== null);
    this.count = this.carousels.at(0)?.slides.length ?? 0;
    const lightbox = this.querySelector(`[${pdpAttributes.lightbox}]`);
    this.lightbox = lightbox instanceof HTMLElement ? lightbox : null;
    if (this.lightbox !== null) {
      document.body.append(this.lightbox);
      this.lightbox.addEventListener("click", this.onClick);
      this.lightbox.addEventListener("keydown", this.onArrowKey);
    }
    this.addEventListener("click", this.onClick);
    this.addEventListener("keydown", this.onArrowKey);
    for (const viewport of viewports) {
      viewport.addEventListener("pointerdown", this.onPointerDown);
      viewport.addEventListener("pointerup", this.onPointerUp);
      viewport.addEventListener("pointercancel", this.onPointerCancel);
    }
    document.addEventListener(MEDIA_CHANGE_EVENT, this.onMediaChange);
    this.render();
  }
  disconnectedCallback() {
    document.removeEventListener(MEDIA_CHANGE_EVENT, this.onMediaChange);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    document.body.classList.remove(BODY_MODAL_CLASS);
    this.lightbox?.remove();
  }
  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element))
      return;
    if (target.closest(`[${pdpAttributes.prev}]`) !== null) {
      this.go(this.index - 1);
      return;
    }
    if (target.closest(`[${pdpAttributes.next}]`) !== null) {
      this.go(this.index + 1);
      return;
    }
    const dot = target.closest(`[${pdpAttributes.dot}]`);
    if (dot instanceof HTMLElement) {
      const index = dotIndex(dot);
      if (index !== null)
        this.go(index);
      return;
    }
    if (target.closest(`[${pdpAttributes.zoom}]`) !== null) {
      this.openLightbox();
      return;
    }
    if (target.closest(`[${pdpAttributes.close}]`) !== null)
      this.closeLightbox();
  };
  onArrowKey = (event) => {
    const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (offset === 0)
      return;
    event.preventDefault();
    this.go(this.index + offset);
  };
  onDocumentKeydown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeLightbox();
      return;
    }
    if (event.key === "Tab")
      this.trapFocus(event);
  };
  onPointerDown = (event) => {
    if (!event.isPrimary)
      return;
    this.pointerStart = event.clientX;
  };
  onPointerUp = (event) => {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (start === null)
      return;
    const delta = event.clientX - start;
    if (Math.abs(delta) < SWIPE_THRESHOLD)
      return;
    this.go(delta < 0 ? this.index + 1 : this.index - 1);
  };
  onPointerCancel = () => {
    this.pointerStart = null;
  };
  onMediaChange = (event) => {
    if (!(event instanceof CustomEvent))
      return;
    const detail = event.detail;
    if (typeof detail !== "number")
      return;
    const mediaId = String(detail);
    const index = this.carousels.at(0)?.slides.findIndex((slide) => slide.getAttribute(pdpAttributes.mediaId) === mediaId) ?? -1;
    if (index >= 0)
      this.go(index);
  };
  go(index) {
    if (this.count === 0)
      return;
    this.index = (index % this.count + this.count) % this.count;
    this.render();
  }
  render() {
    for (const carousel of this.carousels) {
      carousel.track.style.transform = `translate3d(${String(this.index * -100)}%, 0, 0)`;
      carousel.slides.forEach((slide, slideIndex) => {
        slide.setAttribute("aria-hidden", String(slideIndex !== this.index));
      });
      carousel.dots.forEach((dot, index) => {
        dot.setAttribute("aria-current", String(index === this.index));
      });
    }
  }
  openLightbox() {
    const lightbox = this.lightbox;
    if (lightbox === null)
      return;
    const active = document.activeElement;
    this.returnFocus = active instanceof HTMLElement ? active : null;
    lightbox.hidden = false;
    document.body.classList.add(BODY_MODAL_CLASS);
    document.addEventListener("keydown", this.onDocumentKeydown);
    const close = lightbox.querySelector(`button[${pdpAttributes.close}]`);
    if (close instanceof HTMLElement)
      close.focus();
  }
  closeLightbox() {
    const lightbox = this.lightbox;
    if (lightbox === null || lightbox.hidden)
      return;
    lightbox.hidden = true;
    document.body.classList.remove(BODY_MODAL_CLASS);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    this.returnFocus?.focus();
    this.returnFocus = null;
  }
  trapFocus(event) {
    const lightbox = this.lightbox;
    if (lightbox === null)
      return;
    const focusable = htmlElements(lightbox, FOCUSABLE);
    const first = focusable.at(0);
    const last = focusable.at(-1);
    if (first === undefined || last === undefined)
      return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
var registerGallery = () => {
  if (customElements.get("pdp-gallery") === undefined) {
    customElements.define("pdp-gallery", PdpGallery);
  }
};

// src/pages/pdp-v2/types.ts
var LABEL_KEYS = [
  "heading",
  "formatLegend",
  "subscriptionEvery",
  "subscriptionMonthly",
  "oneTimePurchase",
  "servingsEvery",
  "servingsMonthly",
  "servingsOnly",
  "perMonth",
  "perServing",
  "total",
  "savePercentage",
  "chooseHowToPay",
  "payMonthly",
  "payInFull",
  "payMonthlyDetail",
  "restricted",
  "payInFullDetail",
  "zeroInterest",
  "freeShipping",
  "payOverMonths",
  "totalToday",
  "billedMonthly",
  "addToCart",
  "soldOut",
  "quantity",
  "decrease",
  "increase"
];

// src/pages/pdp-v2/parse.ts
var isArray = (value) => Array.isArray(value);
var readNullableNumber = (source, key) => {
  const value = source[key];
  if (value === null || value === undefined || value === "")
    return null;
  if (typeof value === "number")
    return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
var readBoolean = (source, key) => {
  const value = source[key];
  if (typeof value === "boolean")
    return value;
  return value === "true";
};
var parseDeliveryGroupOption = (value) => {
  if (!isRecord(value))
    return null;
  const purchaseOptionKey = readString(value, "purchase-option-key");
  if (purchaseOptionKey === "")
    return null;
  const paymentsPerCycle = readNumber(value, "payments-per-cycle");
  return {
    purchaseOptionKey,
    sellingPlanId: readNullableNumber(value, "selling-plan-id"),
    paymentsPerCycle: Math.max(1, Math.round(paymentsPerCycle))
  };
};
var parseDeliveryGroup = (value) => {
  if (!isRecord(value))
    return null;
  const key = readString(value, "key");
  if (key === "")
    return null;
  const rawOptions = value["options"];
  const options = isArray(rawOptions) ? rawOptions.map(parseDeliveryGroupOption).filter((option) => option !== null) : [];
  if (options.length === 0)
    return null;
  const deliveryMonths = readNullableNumber(value, "delivery-months");
  return {
    key,
    sort: readNumber(value, "sort"),
    deliveryMonths: deliveryMonths === null ? 1 : Math.max(1, Math.round(deliveryMonths)),
    options
  };
};
var parseDeliveryGroups = (value) => {
  if (!isArray(value))
    return [];
  return value.map(parseDeliveryGroup).filter((group) => group !== null).sort((left, right) => left.sort - right.sort);
};
var readNumberArray = (source, key) => {
  const value = source[key];
  if (!isArray(value))
    return [];
  return value.filter((entry) => typeof entry === "number");
};
var readStringArray = (source, key) => {
  const value = source[key];
  if (!isArray(value))
    return [];
  return value.filter((entry) => typeof entry === "string");
};
var readStringMap = (source, key) => {
  const value = source[key];
  if (!isRecord(value))
    return {};
  const map = {};
  for (const [name, entry] of Object.entries(value)) {
    if (typeof entry === "string")
      map[name] = entry;
  }
  return map;
};
var parseServingFormat = (value) => {
  if (!isRecord(value))
    return null;
  const name = readString(value, "value");
  if (name === "")
    return null;
  return {
    value: name,
    label: readString(value, "label") || name,
    description: readString(value, "description"),
    available: readBoolean(value, "available")
  };
};
var parseBadge = (value) => {
  if (!isRecord(value))
    return null;
  const handle = readString(value, "handle");
  if (handle === "")
    return null;
  return {
    handle,
    label: readString(value, "label") || handle,
    sortOrder: readNumber(value, "sortOrder")
  };
};
var parseBadges = (value) => {
  if (!isArray(value))
    return [];
  return value.map(parseBadge).filter((badge) => badge !== null);
};
var parseVariant = (value, purchaseOptionKeys) => {
  if (!isRecord(value))
    return null;
  const id = readNullableNumber(value, "id");
  if (id === null)
    return null;
  const purchaseOption = readString(value, "purchaseOption");
  if (!purchaseOptionKeys.has(purchaseOption))
    return null;
  return {
    id,
    title: readString(value, "title"),
    optionValues: readStringArray(value, "optionValues"),
    sku: readString(value, "sku"),
    globalId: readString(value, "globalId"),
    discountValue: readString(value, "discountValue"),
    available: readBoolean(value, "available"),
    price: readNumber(value, "price"),
    compareAtPrice: readNullableNumber(value, "compareAtPrice"),
    servingCount: readNullableNumber(value, "servingCount"),
    packageType: readString(value, "packageType"),
    planBadges: parseBadges(value["planBadges"]),
    paymentBadge: parseBadge(value["paymentBadge"]),
    servingFormat: readString(value, "servingFormat"),
    compareAtCycle: readNullableNumber(value, "compareAtCycle"),
    sellingPlanIds: readNumberArray(value, "sellingPlanIds"),
    purchaseOption,
    featuredMediaId: readNullableNumber(value, "featuredMediaId")
  };
};
var parseProductData = (value) => {
  if (!isRecord(value))
    return null;
  const rawVariants = value["variants"];
  if (!isArray(rawVariants))
    return null;
  const rawServingFormats = value["servingFormats"];
  const servingFormats = isArray(rawServingFormats) ? rawServingFormats.map(parseServingFormat).filter((format) => format !== null) : [];
  const deliveryGroups = parseDeliveryGroups(value["deliveryGroups"]);
  const purchaseOptionKeys = new Set(deliveryGroups.flatMap((group) => group.options.map((option) => option.purchaseOptionKey)));
  const variants = rawVariants.map((variant) => parseVariant(variant, purchaseOptionKeys)).filter((variant) => variant !== null);
  if (variants.length === 0)
    return null;
  return {
    id: readNumber(value, "id"),
    moneyFormat: readString(value, "moneyFormat") || "${{amount}}",
    oneTimePurchaseDisabled: readBoolean(value, "oneTimePurchaseDisabled"),
    restrictAddToCart: readBoolean(value, "restrictAddToCart"),
    hsfChild: readBoolean(value, "hsfChild"),
    deliveryGroups,
    sellingPlanNames: readStringMap(value, "sellingPlanNames"),
    servingFormats,
    variants
  };
};
var parseLabels = (value) => {
  const source = isRecord(value) ? value : {};
  const entries = LABEL_KEYS.map((key) => [
    key,
    readString(source, key)
  ]);
  const labels = {};
  for (const [key, text] of entries)
    labels[key] = text;
  return {
    heading: labels["heading"] ?? "",
    formatLegend: labels["formatLegend"] ?? "",
    subscriptionEvery: labels["subscriptionEvery"] ?? "",
    subscriptionMonthly: labels["subscriptionMonthly"] ?? "",
    oneTimePurchase: labels["oneTimePurchase"] ?? "",
    servingsEvery: labels["servingsEvery"] ?? "",
    servingsMonthly: labels["servingsMonthly"] ?? "",
    servingsOnly: labels["servingsOnly"] ?? "",
    perMonth: labels["perMonth"] ?? "",
    perServing: labels["perServing"] ?? "",
    total: labels["total"] ?? "",
    savePercentage: labels["savePercentage"] ?? "",
    chooseHowToPay: labels["chooseHowToPay"] ?? "",
    payMonthly: labels["payMonthly"] ?? "",
    payInFull: labels["payInFull"] ?? "",
    payMonthlyDetail: labels["payMonthlyDetail"] ?? "",
    restricted: labels["restricted"] ?? "",
    payInFullDetail: labels["payInFullDetail"] ?? "",
    zeroInterest: labels["zeroInterest"] ?? "",
    freeShipping: labels["freeShipping"] ?? "",
    payOverMonths: labels["payOverMonths"] ?? "",
    totalToday: labels["totalToday"] ?? "",
    billedMonthly: labels["billedMonthly"] ?? "",
    addToCart: labels["addToCart"] ?? "",
    soldOut: labels["soldOut"] ?? "",
    quantity: labels["quantity"] ?? "",
    decrease: labels["decrease"] ?? "",
    increase: labels["increase"] ?? ""
  };
};

// src/pages/pdp-v2/plan-model.ts
var MODE_ORDER = { installments: 0, full: 1 };
var matchesServingFormat = (variant, servingFormat) => {
  if (servingFormat === "" || variant.servingFormat === "")
    return true;
  return variant.servingFormat === servingFormat;
};
var defaultServingFormat = (product) => {
  const formats = product.servingFormats;
  if (formats.length < 2)
    return "";
  return (formats.find((format) => format.available) ?? formats[0])?.value ?? "";
};
var perServing = (total, servings) => {
  if (servings === null || servings <= 0)
    return null;
  return Math.round(total / servings);
};
var savingsAgainst = (compareAt, total) => {
  if (compareAt === null || compareAt <= total)
    return null;
  const percent = Math.round((compareAt - total) / compareAt * 100);
  return percent < 1 ? null : percent;
};
var modeFor = (option) => option.paymentsPerCycle > 1 ? "installments" : "full";
var paymentOptionFrom = (group, option, variant) => {
  const chargeCount = option.paymentsPerCycle;
  const totalPerCycle = variant.price * chargeCount;
  const chargeCompareAt = variant.compareAtCycle ?? variant.compareAtPrice;
  const compareAtPerCharge = chargeCompareAt !== null && chargeCompareAt > variant.price ? chargeCompareAt : null;
  const compareAtTotal = compareAtPerCharge === null ? null : compareAtPerCharge * chargeCount;
  return {
    mode: modeFor(option),
    purchaseOptionKey: option.purchaseOptionKey,
    variantId: variant.id,
    sellingPlanId: option.sellingPlanId,
    amountPerCharge: variant.price,
    chargeCount,
    totalPerCycle,
    compareAtTotal,
    compareAtPerCharge,
    savePercentage: savingsAgainst(compareAtTotal, totalPerCycle),
    pricePerServing: perServing(totalPerCycle, variant.servingCount),
    servingCount: variant.servingCount,
    packageType: variant.packageType,
    paymentBadge: variant.paymentBadge,
    billsMonthly: option.sellingPlanId !== null && (chargeCount > 1 || group.deliveryMonths === 1),
    available: variant.available
  };
};
var isAttached = (variant, option) => {
  if (option.sellingPlanId === null)
    return true;
  if (variant.sellingPlanIds.includes(option.sellingPlanId))
    return true;
  console.warn(`[pdp-v2] variant ${String(variant.id)} declares "${option.purchaseOptionKey}" but is not attached to selling plan ${String(option.sellingPlanId)}`);
  return false;
};
var cardsForGroup = (group, variants) => {
  const cards = [];
  for (const variant of variants) {
    const option = group.options.find((candidate) => candidate.purchaseOptionKey === variant.purchaseOption);
    if (option === undefined || !isAttached(variant, option))
      continue;
    const target = cards.find((card) => !card.some((member) => member.option.purchaseOptionKey === option.purchaseOptionKey));
    if (target === undefined)
      cards.push([{ variant, option }]);
    else
      target.push({ variant, option });
  }
  return cards;
};
var gatherBadges = (members) => {
  const byHandle = new Map;
  for (const member of members) {
    for (const badge of member.variant.planBadges) {
      if (!byHandle.has(badge.handle))
        byHandle.set(badge.handle, badge);
    }
  }
  return [...byHandle.values()].sort((left, right) => left.sortOrder - right.sortOrder);
};
var planFromCard = (group, card) => {
  const members = [...card].sort((left, right) => MODE_ORDER[modeFor(left.option)] - MODE_ORDER[modeFor(right.option)]);
  const lead = members[0];
  if (lead === undefined)
    return null;
  const paymentOptions = members.map((member) => paymentOptionFrom(group, member.option, member.variant));
  return {
    id: `${group.key}:${String(lead.variant.id)}`,
    kind: members.every((member) => member.option.sellingPlanId === null) ? "one-time" : "subscription",
    variantId: lead.variant.id,
    deliveryMonths: group.deliveryMonths,
    servingCount: lead.variant.servingCount,
    packageType: lead.variant.packageType,
    badges: gatherBadges(members),
    servingFormat: lead.variant.servingFormat,
    paymentOptions,
    available: paymentOptions.some((option) => option.available)
  };
};
var oneTimePrice = (plan) => plan.paymentOptions[0]?.totalPerCycle ?? 0;
var sortOneTimeByPrice = (plans) => {
  const slots = plans.flatMap((plan, index) => plan.kind === "one-time" ? [index] : []);
  if (slots.length < 2)
    return plans;
  const sorted = slots.map((slot) => plans[slot]).filter((plan) => plan !== undefined).sort((left, right) => oneTimePrice(right) - oneTimePrice(left));
  const ordered = [...plans];
  slots.forEach((slot, position) => {
    const plan = sorted[position];
    if (plan !== undefined)
      ordered[slot] = plan;
  });
  return ordered;
};
var derivePlans = (product, servingFormat = "") => {
  const scoped = product.variants.filter((variant) => matchesServingFormat(variant, servingFormat));
  return sortOneTimeByPrice(product.deliveryGroups.flatMap((group) => cardsForGroup(group, scoped).map((card) => planFromCard(group, card)).filter((plan) => plan !== null).filter((plan) => plan.kind === "subscription" || !product.oneTimePurchaseDisabled)));
};
var servingFormatForVariant = (product, variantId) => product.variants.find((candidate) => candidate.id === variantId)?.servingFormat ?? null;
var findPlan = (plans, planId) => plans.find((plan) => plan.id === planId) ?? null;
var planGroupKey = (planId) => {
  const separator = planId.lastIndexOf(":");
  return separator === -1 ? planId : planId.slice(0, separator);
};
var findPlanInGroup = (plans, planId) => {
  const wanted = planGroupKey(planId);
  return plans.find((plan) => planGroupKey(plan.id) === wanted) ?? null;
};
var resolvePaymentOption = (plan, mode) => {
  const requested = plan.paymentOptions.find((option) => option.mode === mode);
  if (requested?.available)
    return requested;
  return plan.paymentOptions.find((option) => option.available) ?? requested ?? null;
};
var defaultSelection = (plans, servingFormat = "") => {
  const plan = plans.find((candidate) => candidate.available) ?? plans[0];
  if (plan === undefined)
    return null;
  const option = resolvePaymentOption(plan, "installments");
  return {
    planId: plan.id,
    mode: option?.mode ?? "full",
    quantity: 1,
    servingFormat
  };
};
var amountDueToday = (option, quantity) => option.amountPerCharge * Math.max(1, quantity);

// src/pages/pdp-v2/url-state.ts
var PARAM_VARIANT = "variant";
var PARAM_SUBSCRIPTION = "subscription";
var PARAM_PAYMENT_TYPE = "payment_type";
var PARAM_SERVINGS = "servings";
var PARAM_FORMAT = "serving_format";
var PARAM_QUANTITY = "quantity";
var PAYMENT_TYPE_BY_MODE = {
  installments: "installment",
  full: "normal"
};
var decode = (value) => value.replace(/\+/g, " ").trim();
var normalize = (value) => decode(value).toLowerCase();
var readInteger = (value) => {
  if (value === null)
    return null;
  const parsed = Number.parseInt(decode(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};
var matchByVariant = (plans, variantId, wantsSubscription) => {
  for (const plan of plans) {
    if (wantsSubscription !== null && plan.kind === "subscription" !== wantsSubscription) {
      continue;
    }
    for (const option of plan.paymentOptions) {
      if (option.variantId === variantId)
        return { plan, option };
    }
  }
  return null;
};
var matchesServings = (plan, servings) => {
  if (plan.servingCount === null)
    return false;
  const target = Number.parseInt(decode(servings), 10);
  if (!Number.isFinite(target))
    return false;
  return plan.servingCount === target;
};
var modeFromPaymentType = (paymentType) => {
  const value = paymentType === null ? "" : normalize(paymentType);
  if (value === "installment")
    return "installments";
  if (value === "normal")
    return "full";
  return null;
};
var readSelection = (search, product, plansFor, fallbackServingFormat) => {
  const params = new URLSearchParams(search);
  const variantId = readInteger(params.get(PARAM_VARIANT));
  const paramFormat = params.get(PARAM_FORMAT);
  const servingFormat = fallbackServingFormat === "" ? "" : (variantId === null ? null : servingFormatForVariant(product, variantId)) || (paramFormat === null ? "" : decode(paramFormat)) || fallbackServingFormat;
  const plans = plansFor(servingFormat);
  if (plans.length === 0)
    return null;
  const quantity = readInteger(params.get(PARAM_QUANTITY));
  const requestedMode = modeFromPaymentType(params.get(PARAM_PAYMENT_TYPE));
  const subscription = params.get(PARAM_SUBSCRIPTION);
  const wantsSubscription = subscription === null ? null : normalize(subscription) === "true";
  const byVariant = variantId === null ? null : matchByVariant(plans, variantId, wantsSubscription);
  if (byVariant !== null) {
    return {
      planId: byVariant.plan.id,
      mode: requestedMode ?? byVariant.option.mode,
      quantity: quantity === null ? 1 : Math.max(1, quantity),
      servingFormat
    };
  }
  const servings = params.get(PARAM_SERVINGS);
  if (subscription === null && servings === null && paramFormat === null)
    return null;
  const candidates = plans.filter((plan) => {
    if (wantsSubscription !== null) {
      const isSubscription = plan.kind === "subscription";
      if (isSubscription !== wantsSubscription)
        return false;
    }
    if (servings !== null && !matchesServings(plan, servings))
      return false;
    return true;
  });
  const plan = candidates.find((candidate) => candidate.available) ?? candidates[0];
  if (plan === undefined)
    return null;
  return {
    planId: plan.id,
    mode: requestedMode ?? "installments",
    quantity: quantity === null ? 1 : Math.max(1, quantity),
    servingFormat
  };
};
var buildSearch = (search, plan, option, selection) => {
  const params = new URLSearchParams(search);
  params.set(PARAM_VARIANT, String(option.variantId));
  params.set(PARAM_SUBSCRIPTION, plan.kind === "subscription" ? "true" : "false");
  params.set(PARAM_PAYMENT_TYPE, PAYMENT_TYPE_BY_MODE[option.mode]);
  if (plan.servingCount === null) {
    params.delete(PARAM_SERVINGS);
  } else {
    params.set(PARAM_SERVINGS, String(plan.servingCount));
  }
  if (selection.servingFormat === "") {
    params.delete(PARAM_FORMAT);
  } else {
    params.set(PARAM_FORMAT, selection.servingFormat);
  }
  if (selection.quantity > 1) {
    params.set(PARAM_QUANTITY, String(selection.quantity));
  } else {
    params.delete(PARAM_QUANTITY);
  }
  return params.toString();
};

// src/pages/pdp-v2/bootstrap.ts
var initialState = (host, search) => {
  const product = parseProductData(readJsonScript(host, `[${pdpAttributes.product}]`));
  if (product === null)
    return null;
  const defaultFormat = defaultServingFormat(product);
  const plansFor = (servingFormat) => derivePlans(product, servingFormat);
  const restored = readSelection(search, product, plansFor, defaultFormat);
  const servingFormat = restored?.servingFormat ?? defaultFormat;
  const plans = plansFor(servingFormat);
  return {
    product,
    labels: parseLabels(readJsonScript(host, `[${pdpAttributes.labels}]`)),
    plans,
    selection: restored !== null && findPlan(plans, restored.planId) !== null ? restored : defaultSelection(plans, servingFormat)
  };
};

// src/pages/pdp-v2/toast.ts
var VISIBLE_MS = 5000;
var FADE_MS = 220;
var FADE_EASING = "ease";
var GAP = 9;
var TOLERANCE = 0.5;
var hideTimer = null;
var frame = null;
var fade = null;
var applied = GAP;
var targetBottomEdge = () => {
  const bar = document.querySelector(`[${pdpAttributes.dock}][${pdpAttributes.stuck}] .pdp-summary__bar`);
  const limit = bar instanceof HTMLElement ? bar.getBoundingClientRect().top : window.innerHeight;
  return limit - GAP;
};
var anchor = (toast) => {
  const drift = toast.getBoundingClientRect().bottom - targetBottomEdge();
  if (Math.abs(drift) < TOLERANCE)
    return;
  applied = Math.max(GAP, Math.round(applied + drift));
  toast.style.setProperty("--pdp-toast-bottom", `${String(applied)}px`);
};
var stopTracking = () => {
  if (frame !== null)
    window.cancelAnimationFrame(frame);
  frame = null;
};
var track = (toast) => {
  anchor(toast);
  frame = window.requestAnimationFrame(() => {
    track(toast);
  });
};
var dismiss = (toast) => {
  const settle = () => {
    toast.hidden = true;
    fade = null;
    stopTracking();
  };
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
    settle();
    return;
  }
  fade = toast.animate({ opacity: [1, 0] }, { duration: FADE_MS, easing: FADE_EASING });
  fade.addEventListener("finish", settle);
};
var showAddedToCart = () => {
  const toast = document.querySelector(`[${pdpAttributes.toast}]`);
  if (!(toast instanceof HTMLElement))
    return;
  fade?.cancel();
  fade = null;
  stopTracking();
  toast.hidden = false;
  anchor(toast);
  anchor(toast);
  track(toast);
  if (hideTimer !== null)
    window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => {
    hideTimer = null;
    dismiss(toast);
  }, VISIBLE_MS);
};

// src/pages/pdp-v2/view/effects.ts
var RESIZE_DURATION = 260;
var RESIZE_EASING = "cubic-bezier(0.22, 0.61, 0.36, 1)";
var fitChips = (root) => {
  for (const chip of root.querySelectorAll(".pdp-plan__chip")) {
    if (!(chip instanceof HTMLElement))
      continue;
    chip.style.width = "";
    const range = document.createRange();
    range.selectNodeContents(chip);
    const rects = [...range.getClientRects()];
    if (rects.length < 2)
      continue;
    const widest = Math.max(...rects.map((rect) => rect.width));
    const style = getComputedStyle(chip);
    const padding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
    chip.style.width = `${String(Math.ceil(widest + padding))}px`;
  }
};
var animateHeight = (element, from) => {
  if (matchMedia("(prefers-reduced-motion: reduce)").matches)
    return;
  const to = element.getBoundingClientRect().height;
  if (Math.abs(to - from) < 1)
    return;
  const overflow = element.style.overflow;
  element.style.overflow = "hidden";
  const animation = element.animate({ height: [`${String(Math.round(from))}px`, `${String(Math.round(to))}px`] }, { duration: RESIZE_DURATION, easing: RESIZE_EASING });
  animation.addEventListener("finish", () => {
    element.style.overflow = overflow;
  });
};
var measureAnimKeys = (root) => {
  const heights = new Map;
  for (const node of root.querySelectorAll(`[${pdpAttributes.animKey}]`)) {
    const key = node instanceof HTMLElement ? node.getAttribute(pdpAttributes.animKey) ?? undefined : undefined;
    if (key !== undefined)
      heights.set(key, node.getBoundingClientRect().height);
  }
  return heights;
};
var mountPlans = (host, next) => {
  const previous = host.querySelector(".pdp-plans");
  if (previous === null) {
    host.querySelector(".pdp-plans-skeleton")?.remove();
    host.append(next);
    fitChips(next);
    return;
  }
  const heights = measureAnimKeys(previous);
  const anchor = captureFocus(host);
  previous.replaceWith(next);
  anchor.restore();
  for (const node of next.querySelectorAll(`[${pdpAttributes.animKey}]`)) {
    if (!(node instanceof HTMLElement))
      continue;
    const key = node.getAttribute(pdpAttributes.animKey) ?? undefined;
    const from = key === undefined ? undefined : heights.get(key);
    if (from !== undefined)
      animateHeight(node, from);
  }
  fitChips(next);
};
var watchChipFit = (host) => {
  let lastWidth = -1;
  requestAnimationFrame(() => {
    fitChips(host);
  });
  document.fonts.ready.then(() => {
    fitChips(host);
  });
  const observer = new ResizeObserver((entries) => {
    const width = entries[0]?.contentRect.width ?? 0;
    if (Math.abs(width - lastWidth) < 1)
      return;
    lastWidth = width;
    fitChips(host);
  });
  observer.observe(host);
  return {
    stop: () => {
      observer.disconnect();
    }
  };
};

// src/pages/pdp-v2/view/markup.ts
var template = (pattern, values) => pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => values[key] ?? match);
var templateInto = (target, pattern, values, strongKeys = []) => {
  for (const part of pattern.split(/(\{\{\s*\w+\s*\}\})/g)) {
    if (part === "")
      continue;
    const match = /^\{\{\s*(\w+)\s*\}\}$/.exec(part);
    const key = match?.[1];
    if (key === undefined) {
      target.append(document.createTextNode(part));
      continue;
    }
    const value = values[key] ?? part;
    if (strongKeys.includes(key)) {
      const strong = document.createElement("strong");
      strong.textContent = value;
      target.append(strong);
    } else {
      target.append(document.createTextNode(value));
    }
  }
};
var checkIcon = () => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 13 13");
  svg.setAttribute("fill", "none");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  svg.setAttribute("class", "pdp-plan__perk-icon");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M10.8327 3.25L4.87435 9.20833L2.16602 6.5");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.19167");
  svg.append(path);
  return svg;
};

// src/pages/pdp-v2/view/payment-options.ts
var renderPaymentOption = (props, paymentOption) => {
  const { plan, selectedMode, labels, money, actions } = props;
  const isInstallments = paymentOption.mode === "installments";
  const resolved = resolvePaymentOption(plan, selectedMode);
  const isSelected = resolved?.mode === paymentOption.mode;
  const label = document.createElement("label");
  label.className = "pdp-payment__option";
  if (isSelected)
    label.setAttribute(pdpAttributes.selected, "");
  const input = document.createElement("input");
  input.type = "radio";
  input.name = `pdp-payment-mode-${paymentOption.mode}`;
  input.value = paymentOption.mode;
  input.checked = isSelected === true;
  input.disabled = !paymentOption.available;
  input.className = "pdp-radio pdp-payment__input";
  input.addEventListener("change", () => actions.onSelectMode(paymentOption.mode));
  const title = document.createElement("span");
  title.className = "pdp-payment__title";
  title.textContent = isInstallments ? labels.payMonthly : labels.payInFull;
  const detail = document.createElement("span");
  detail.className = "pdp-payment__detail";
  detail.textContent = isInstallments ? template(labels.payMonthlyDetail, {
    count: String(paymentOption.chargeCount),
    amount: money(paymentOption.amountPerCharge),
    total: money(paymentOption.totalPerCycle)
  }) : template(labels.payInFullDetail, {
    amount: money(paymentOption.totalPerCycle),
    count: String(plan.deliveryMonths)
  });
  const text = document.createElement("span");
  text.className = "pdp-payment__text";
  text.append(title, detail);
  label.append(input, text);
  if (paymentOption.paymentBadge !== null) {
    const flag = document.createElement("span");
    flag.className = "pdp-payment__flag";
    flag.textContent = paymentOption.paymentBadge.label;
    label.append(flag);
  }
  return label;
};
var renderPaymentOptions = (props) => {
  const wrapper = document.createElement("div");
  wrapper.className = "pdp-payment";
  const legend = document.createElement("p");
  legend.className = "pdp-payment__legend";
  legend.textContent = props.labels.chooseHowToPay;
  wrapper.append(legend);
  const options = document.createElement("div");
  options.className = "pdp-payment__options";
  options.setAttribute("role", "radiogroup");
  options.setAttribute("aria-label", props.labels.chooseHowToPay);
  for (const paymentOption of props.plan.paymentOptions) {
    options.append(renderPaymentOption(props, paymentOption));
  }
  wrapper.append(options);
  return wrapper;
};

// src/pages/pdp-v2/view/plan-card.ts
var displayOption = (plan, isSelected, mode) => resolvePaymentOption(plan, isSelected ? mode : "installments");
var planTitle = (plan, labels) => {
  if (plan.kind === "one-time")
    return labels.oneTimePurchase;
  if (plan.deliveryMonths <= 1)
    return labels.subscriptionMonthly;
  return template(labels.subscriptionEvery, { count: String(plan.deliveryMonths) });
};
var chipText = (plan, option, labels) => {
  const servingCount = option?.servingCount ?? plan.servingCount;
  if (servingCount === null)
    return "";
  const count = String(servingCount);
  const base = plan.kind === "one-time" ? template(labels.servingsOnly, { count }) : plan.deliveryMonths <= 1 ? template(labels.servingsMonthly, { count }) : template(labels.servingsEvery, { count, months: String(plan.deliveryMonths) });
  const packageType = option?.packageType ?? plan.packageType;
  return packageType === "" ? base : `${base} (${packageType})`;
};
var renderCardBadges = (props) => {
  const badges = props.plan.badges;
  if (badges.length === 0)
    return null;
  const strip = document.createElement("p");
  strip.className = "pdp-plan__banner";
  badges.forEach((badge, index) => {
    if (index > 0) {
      const dot = document.createElement("span");
      dot.className = "pdp-plan__banner-dot";
      dot.setAttribute("aria-hidden", "true");
      strip.append(dot);
    }
    const text = document.createElement("span");
    text.textContent = badge.label;
    strip.append(text);
  });
  return strip;
};
var renderPrices = (option, labels, money) => {
  const prices = document.createElement("span");
  prices.className = "pdp-plan__prices";
  if (option === null)
    return prices;
  const headline = document.createElement("span");
  headline.className = "pdp-plan__headline";
  const amount = document.createElement("span");
  amount.className = "pdp-plan__amount";
  amount.textContent = money(option.amountPerCharge);
  headline.append(amount);
  if (option.compareAtPerCharge !== null) {
    const compare = document.createElement("s");
    compare.className = "pdp-plan__compare";
    compare.textContent = money(option.compareAtPerCharge);
    headline.append(compare);
  }
  prices.append(headline);
  if (option.pricePerServing !== null) {
    const perServing = document.createElement("span");
    perServing.className = "pdp-plan__per-serving";
    perServing.textContent = template(labels.perServing, {
      amount: money(option.pricePerServing)
    });
    prices.append(perServing);
  }
  return prices;
};
var renderPlanCard = (props) => {
  const { plan, isSelected, selectedMode, labels, money, actions } = props;
  const card = document.createElement("div");
  card.className = "pdp-plan";
  card.setAttribute(pdpAttributes.animKey, plan.id);
  if (isSelected)
    card.setAttribute(pdpAttributes.selected, "");
  if (!plan.available)
    card.setAttribute(pdpAttributes.unavailable, "");
  const badges = renderCardBadges(props);
  if (badges !== null)
    card.append(badges);
  const body = document.createElement("label");
  body.className = "pdp-plan__body";
  const input = document.createElement("input");
  input.type = "radio";
  input.name = `pdp-plan-${plan.id}`;
  input.value = plan.id;
  input.checked = isSelected;
  input.disabled = !plan.available;
  input.className = "pdp-radio pdp-plan__input";
  input.addEventListener("change", () => actions.onSelectPlan(plan.id));
  const head = document.createElement("span");
  head.className = "pdp-plan__head";
  const title = document.createElement("span");
  title.className = "pdp-plan__title";
  title.textContent = planTitle(plan, labels);
  head.append(title);
  const displayed = displayOption(plan, isSelected, selectedMode);
  const savePercentage = displayed?.savePercentage ?? null;
  if (savePercentage !== null) {
    const save = document.createElement("span");
    save.className = "pdp-plan__save";
    save.textContent = template(labels.savePercentage, { percent: String(savePercentage) });
    head.append(save);
  }
  const content = document.createElement("span");
  content.className = "pdp-plan__content";
  content.append(renderPrices(displayed, labels, money), head);
  body.append(input, content);
  const chip = chipText(plan, displayed, labels);
  if (chip !== "") {
    const element = document.createElement("span");
    element.className = "pdp-plan__chip";
    element.textContent = chip;
    content.append(element);
  }
  if (isSelected && plan.kind === "subscription" && plan.deliveryMonths > 1) {
    const perks = document.createElement("ul");
    perks.className = "pdp-plan__perks";
    for (const perk of [
      labels.freeShipping,
      template(labels.payOverMonths, { count: String(plan.deliveryMonths) })
    ]) {
      const item = document.createElement("li");
      item.className = "pdp-plan__perk";
      const text = document.createElement("span");
      text.textContent = perk;
      item.append(checkIcon(), text);
      perks.append(item);
    }
    content.append(perks);
  }
  card.append(body);
  if (isSelected && plan.paymentOptions.length > 1) {
    card.setAttribute(pdpAttributes.expanded, "");
    card.append(renderPaymentOptions({ plan, selectedMode, labels, money, actions }));
  }
  return card;
};

// src/pages/pdp-v2/view/serving-formats.ts
var renderServingFormats = (props) => {
  const { servingFormats, selection, labels, actions } = props;
  if (servingFormats.length < 2)
    return null;
  const current = servingFormats.find((format) => format.value === selection.servingFormat);
  const wrapper = document.createElement("fieldset");
  wrapper.className = "pdp-formats";
  const legend = document.createElement("legend");
  legend.className = "pdp-formats__legend";
  templateInto(legend, labels.formatLegend, { value: current?.label ?? "" }, ["value"]);
  wrapper.append(legend);
  const row = document.createElement("div");
  row.className = "pdp-formats__row";
  row.setAttribute("role", "radiogroup");
  for (const format of servingFormats) {
    const label = document.createElement("label");
    label.className = "pdp-format";
    if (format.value === selection.servingFormat)
      label.setAttribute(pdpAttributes.selected, "");
    if (!format.available)
      label.setAttribute(pdpAttributes.unavailable, "");
    const input = document.createElement("input");
    input.type = "radio";
    input.name = `pdp-serving-format-${format.value}`;
    input.value = format.value;
    input.checked = format.value === selection.servingFormat;
    input.disabled = !format.available;
    input.className = "pdp-radio pdp-format__input";
    input.addEventListener("change", () => actions.onSelectFormat(format.value));
    const title = document.createElement("span");
    title.className = "pdp-format__title";
    title.textContent = format.label;
    label.append(input, title);
    if (format.description !== "") {
      const description = document.createElement("span");
      description.className = "pdp-format__description";
      description.textContent = format.description;
      label.append(description);
    }
    row.append(label);
  }
  wrapper.append(row);
  return wrapper;
};

// src/pages/pdp-v2/view/summary.ts
var renderActions = (props) => {
  const { plan, option, selection, labels, money, submitting, restricted, actions: on } = props;
  const actions = document.createElement("div");
  actions.className = "pdp-actions";
  const quantity = document.createElement("div");
  quantity.className = "pdp-quantity";
  quantity.setAttribute("role", "group");
  quantity.setAttribute("aria-label", labels.quantity);
  const decrease = document.createElement("button");
  decrease.type = "button";
  decrease.className = "pdp-quantity__button";
  decrease.textContent = "−";
  decrease.setAttribute("aria-label", labels.decrease);
  decrease.disabled = selection.quantity <= 1;
  decrease.addEventListener("click", () => on.onChangeQuantity(-1));
  const value = document.createElement("output");
  value.className = "pdp-quantity__value";
  value.textContent = String(selection.quantity);
  const increase = document.createElement("button");
  increase.type = "button";
  increase.className = "pdp-quantity__button";
  increase.textContent = "+";
  increase.setAttribute("aria-label", labels.increase);
  increase.addEventListener("click", () => on.onChangeQuantity(1));
  quantity.append(decrease, value, increase);
  const submit = document.createElement("button");
  submit.type = "button";
  submit.className = "pdp-submit";
  submit.disabled = !plan.available || !option.available || restricted;
  submit.setAttribute("aria-disabled", String(submit.disabled || submitting));
  submit.setAttribute("aria-busy", String(submitting));
  submit.setAttribute(pdpAttributes.loading, String(submitting));
  const dueToday = amountDueToday(option, selection.quantity);
  submit.textContent = plan.available ? `${labels.addToCart} · ${money(dueToday)}` : labels.soldOut;
  submit.addEventListener("click", () => on.onSubmit());
  actions.append(quantity, submit);
  return actions;
};
var renderSummary = (props) => {
  const { plan, option, selection, labels, money, stuck } = props;
  const summary = document.createElement("div");
  summary.className = "pdp-summary";
  const dueToday = amountDueToday(option, selection.quantity);
  const anchor = document.createElement("div");
  anchor.className = "pdp-summary__anchor";
  anchor.setAttribute(pdpAttributes.stickyAnchor, "");
  summary.append(anchor);
  const totalRow = document.createElement("div");
  totalRow.className = "pdp-summary__row";
  const totalLabel = document.createElement("span");
  totalLabel.className = "pdp-summary__label";
  totalLabel.textContent = labels.totalToday;
  const totalValue = document.createElement("strong");
  totalValue.className = "pdp-summary__value";
  totalValue.textContent = money(dueToday);
  totalRow.append(totalLabel, totalValue);
  summary.append(totalRow);
  const dock = document.createElement("div");
  dock.className = "pdp-summary__dock";
  dock.setAttribute(pdpAttributes.dock, "");
  if (stuck)
    dock.setAttribute(pdpAttributes.stuck, "");
  const bar = document.createElement("div");
  bar.className = "pdp-summary__bar";
  dock.append(bar);
  const note = document.createElement("p");
  note.className = "pdp-summary__note";
  note.setAttribute(pdpAttributes.animKey, "summary-note");
  if (option.mode === "installments") {
    templateInto(note, labels.billedMonthly, {
      amount: money(option.totalPerCycle * selection.quantity),
      count: String(plan.deliveryMonths)
    }, ["amount"]);
  }
  bar.append(note, renderActions(props));
  summary.append(dock);
  return summary;
};

// src/pages/pdp-v2/view/plans-view.ts
var renderPlans = (props) => {
  const { plans, plan, option, selection, labels, money, actions } = props;
  const root = document.createElement("div");
  root.className = "pdp-plans";
  const formats = renderServingFormats({
    servingFormats: props.servingFormats,
    selection,
    labels,
    actions
  });
  if (formats !== null)
    root.append(formats);
  const heading = document.createElement("h2");
  heading.className = "pdp-plans__heading";
  heading.textContent = labels.heading;
  root.append(heading);
  const list = document.createElement("div");
  list.className = "pdp-plans__list";
  list.setAttribute("role", "radiogroup");
  list.setAttribute("aria-label", labels.heading);
  for (const candidate of plans) {
    list.append(renderPlanCard({
      plan: candidate,
      plans,
      isSelected: candidate.id === selection.planId,
      selectedMode: selection.mode,
      labels,
      money,
      actions
    }));
  }
  root.append(list);
  if (plan !== null && option !== null) {
    root.append(renderSummary({
      plan,
      option,
      selection,
      labels,
      money,
      stuck: props.stuck,
      submitting: props.submitting,
      restricted: props.restricted,
      actions
    }));
  }
  const error = document.createElement("p");
  error.className = "pdp-plans__error";
  error.setAttribute("role", "alert");
  error.setAttribute(pdpAttributes.error, "");
  error.hidden = true;
  root.append(error);
  return root;
};

// src/pages/pdp-v2/plan-selector.ts
var STICKY_REACH = 1e5;

class PdpPlanSelector extends HTMLElement {
  product = null;
  labels = null;
  plans = [];
  selection = null;
  submitting = false;
  chipFit = null;
  stickyObserver = null;
  stuck = false;
  mediaVariantId = null;
  actions = {
    onSelectPlan: (planId) => {
      this.selectPlan(planId);
    },
    onSelectFormat: (servingFormat) => {
      this.selectFormat(servingFormat);
    },
    onSelectMode: (mode) => {
      this.selectMode(mode);
    },
    onChangeQuantity: (delta) => {
      this.changeQuantity(delta);
    },
    onSubmit: () => {
      this.submit();
    }
  };
  connectedCallback() {
    const state = initialState(this, window.location.search);
    if (state === null) {
      this.querySelector(".pdp-plans-skeleton")?.remove();
      return;
    }
    this.product = state.product;
    this.labels = state.labels;
    this.plans = state.plans;
    this.selection = state.selection;
    this.addEventListener("keydown", arrowNavigate);
    this.render();
    this.chipFit = watchChipFit(this);
  }
  disconnectedCallback() {
    this.chipFit?.stop();
    this.chipFit = null;
    this.stickyObserver?.disconnect();
    this.stickyObserver = null;
  }
  get money() {
    const format = this.product?.moneyFormat ?? "${{amount}}";
    return (cents) => formatMoney(cents, format);
  }
  selectedPlan() {
    if (this.selection === null)
      return null;
    return findPlan(this.plans, this.selection.planId);
  }
  selectedOption() {
    const plan = this.selectedPlan();
    if (plan === null || this.selection === null)
      return null;
    return resolvePaymentOption(plan, this.selection.mode);
  }
  get restricted() {
    return this.product?.restrictAddToCart === true || this.product?.hsfChild === true;
  }
  selectPlan(planId) {
    if (this.selection === null || this.selection.planId === planId)
      return;
    const plan = findPlan(this.plans, planId);
    if (plan === null)
      return;
    this.selection = { ...this.selection, planId };
    this.render();
  }
  selectFormat(servingFormat) {
    const product = this.product;
    const selection = this.selection;
    if (product === null || selection === null)
      return;
    if (selection.servingFormat === servingFormat)
      return;
    this.plans = derivePlans(product, servingFormat);
    const kept = findPlanInGroup(this.plans, selection.planId);
    this.selection = kept === null ? defaultSelection(this.plans, servingFormat) : { ...selection, planId: kept.id, servingFormat };
    this.render();
  }
  selectMode(mode) {
    if (this.selection === null || this.selection.mode === mode)
      return;
    this.selection = { ...this.selection, mode };
    this.render();
  }
  changeQuantity(delta) {
    if (this.selection === null)
      return;
    const next = Math.max(1, this.selection.quantity + delta);
    if (next === this.selection.quantity)
      return;
    this.selection = { ...this.selection, quantity: next };
    this.render();
  }
  async submit() {
    const option = this.selectedOption();
    const plan = this.selectedPlan();
    if (option === null || plan === null || this.selection === null || this.submitting)
      return;
    if (this.restricted) {
      this.showError(this.labels?.restricted ?? "");
      return;
    }
    this.submitting = true;
    this.render();
    const variant = this.product?.variants.find((candidate) => candidate.id === option.variantId) ?? null;
    const properties = {
      _globalId: variant?.globalId ?? "",
      _discountvalue: variant?.discountValue ?? "",
      _selling_plan_name: "",
      _sellingplan: "",
      _ots_installment: "false"
    };
    if (option.sellingPlanId !== null) {
      properties["_monthly_base_total"] = formatDecimal(option.totalPerCycle);
      properties["_subscriptionType"] = subscriptionTypeFor(option.mode);
      properties["_selling_plan_name"] = this.product?.sellingPlanNames[String(option.sellingPlanId)] ?? "";
    }
    const item = [
      {
        variantId: option.variantId,
        quantity: this.selection.quantity,
        sellingPlanId: option.sellingPlanId,
        properties
      }
    ];
    const result = await cart.add(item);
    this.submitting = false;
    this.render();
    if (result.ok) {
      announceAddedToCart();
      if (isMobileViewport())
        showAddedToCart();
      else
        openCartDrawer();
    }
    if (!result.ok && result.reason === "failed")
      this.showError(readableCartMessage(result.message));
  }
  showError(message) {
    if (message === "")
      return;
    const target = this.querySelector(`[${pdpAttributes.error}]`);
    if (target === null)
      return;
    target.textContent = message;
    target.removeAttribute("hidden");
  }
  render() {
    const labels = this.labels;
    const selection = this.selection;
    if (labels === null || selection === null)
      return;
    const plan = this.selectedPlan();
    const option = this.selectedOption();
    mountPlans(this, renderPlans({
      plans: this.plans,
      plan,
      option,
      selection,
      labels,
      money: this.money,
      servingFormats: this.product?.servingFormats ?? [],
      stuck: this.stuck,
      submitting: this.submitting,
      restricted: this.restricted,
      actions: this.actions
    }));
    this.watchSticky();
    if (plan !== null && option !== null)
      this.syncUrl(plan, option, selection);
    if (option !== null)
      this.syncMedia(option.variantId);
  }
  syncMedia(variantId) {
    if (this.mediaVariantId === variantId)
      return;
    this.mediaVariantId = variantId;
    const mediaId = this.product?.variants.find((variant) => variant.id === variantId)?.featuredMediaId ?? null;
    if (mediaId === null)
      return;
    document.dispatchEvent(new CustomEvent(MEDIA_CHANGE_EVENT, { detail: mediaId }));
  }
  watchSticky() {
    this.stickyObserver?.disconnect();
    this.stickyObserver = null;
    if (this.stuck)
      return;
    const anchor = this.querySelector(`[${pdpAttributes.stickyAnchor}]`);
    if (!(anchor instanceof HTMLElement))
      return;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry === undefined || !entry.isIntersecting)
        return;
      this.setStuck();
      this.stickyObserver?.disconnect();
      this.stickyObserver = null;
    }, { threshold: 0, rootMargin: `${String(STICKY_REACH)}px 0px 0px 0px` });
    observer.observe(anchor);
    this.stickyObserver = observer;
  }
  setStuck() {
    this.stuck = true;
    const dock = this.querySelector(`[${pdpAttributes.dock}]`);
    if (!(dock instanceof HTMLElement))
      return;
    dock.setAttribute(pdpAttributes.stuck, "");
  }
  syncUrl(plan, option, selection) {
    const search = buildSearch(window.location.search, plan, option, selection);
    const url = `${window.location.pathname}?${search}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", url);
  }
}
var registerPlanSelector = () => {
  if (customElements.get("pdp-plan-selector") === undefined) {
    customElements.define("pdp-plan-selector", PdpPlanSelector);
  }
};

// src/pages/pdp-v2/index.ts
registerGallery();
registerPlanSelector();
